import React, { useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

const ReCaptchaComponent = forwardRef(({ 
  siteKey, 
  theme = 'light', 
  size = 'invisible', // Default to invisible for v3
  version = 'v3', // v2 or v3
  action = 'submit', // Action for v3
  onVerify,
  onExpire,
  onError,
  className = '',
  style = {},
  autoExecute = true // Auto execute for v3
}, ref) => {
  const recaptchaRef = useRef(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    },
    getValue: () => {
      if (recaptchaRef.current) {
        return recaptchaRef.current.getValue();
      }
      return null;
    },
    execute: () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.execute();
      }
    }
  }));

  // Auto execute for v3 on component mount
  useEffect(() => {
    if (version === 'v3' && autoExecute && recaptchaRef.current) {
      recaptchaRef.current.execute();
    }
  }, [version, autoExecute]);

  const handleChange = (value) => {
    if (onVerify) {
      onVerify(value);
    }
  };

  const handleExpired = () => {
    if (onExpire) {
      onExpire();
    }
  };

  const handleError = (error) => {
    if (onError) {
      onError(error);
    }
  };

  // For v3, we don't render the widget visibly
  if (version === 'v3') {
    return (
      <div className={`recaptcha-wrapper recaptcha-v3 ${className}`} style={style}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={siteKey}
          size="invisible"
          onChange={handleChange}
          onExpired={handleExpired}
          onErrored={handleError}
        />
      </div>
    );
  }

  // For v2, render the visible widget
  return (
    <div className={`recaptcha-wrapper recaptcha-v2 ${className}`} style={style}>
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={siteKey}
        theme={theme}
        size={size}
        onChange={handleChange}
        onExpired={handleExpired}
        onErrored={handleError}
      />
    </div>
  );
});

ReCaptchaComponent.displayName = 'ReCaptchaComponent';

export default ReCaptchaComponent;
